import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../lib/api';
import { Button } from '../ui/controls';
import { ConfirmDialog, RenameDialog } from '../ui/dialog';
import { ErrorNotice, useToast } from '../ui/feedback';
import { Menu } from '../ui/menu';
import { DataTable, PageHead, Tabs } from '../ui/table';
import { DefList, Mono, Num, Tag, Time } from '../ui/values';
import { AreasTab, AuditTab, BackLink, ChestTab, PlansTab } from './common';
import { paths } from './paths';
import type { AdminTeamDetail } from './types';

/** Team-Detail (design.md §6): Reiter Mitglieder · Kisten · Audit. Die Join-ID steht nur hier (§9). */

type TabKey = 'members' | 'chests' | 'areas' | 'plans' | 'audit';
type Action = 'rename' | 'rotate' | 'dissolve' | { remove: { userId: string; name: string; isOwner: boolean } };

export function TeamDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('members');
  const [action, setAction] = useState<Action | null>(null);
  const key = ['admin', 'team', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminTeamDetail>(`/admin/teams/${id}`) });
  const t = q.data;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    await qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
  };

  if (q.isError) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!t) return <p className="muted">Lädt…</p>;
  const owner = t.members.find((m) => m.isOwner);
  const remove = typeof action === 'object' && action !== null ? action.remove : null;

  return (
    <div className="stack-lg">
      <PageHead
        back={<BackLink to={paths.teams} label="Teams" />}
        title={t.name}
        context={`${t.members.length} Mitglieder · ${t.chestCount} Kisten`}
        actions={
          <>
            <Button onClick={() => setAction('rename')}>Umbenennen…</Button>
            <Button onClick={() => setAction('rotate')}>Join-ID erneuern…</Button>
            <Menu items={[{ label: 'Team auflösen…', danger: true, onSelect: () => setAction('dissolve') }]} />
          </>
        }
      />

      <DefList
        items={[
          ['Besitzer', owner ? <Link to={paths.player(owner.userId)}>{owner.name ?? owner.userId}</Link> : '–'],
          ['Join-ID', <Mono value={t.joinCode} />],
          ['Mitglieder', <Num value={t.members.length} />],
          ['Kisten', <Num value={t.chestCount} />],
          ['Erstellt', <Time iso={t.createdAt} absolute />],
        ]}
      />

      <div className="stack">
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'members', label: 'Mitglieder', count: t.members.length },
            { key: 'chests', label: 'Kisten', count: t.chestCount },
            { key: 'areas', label: 'Areas' },
            { key: 'plans', label: 'Sortierpläne' },
            { key: 'audit', label: 'Audit' },
          ]}
        />
        {tab === 'members' && (
          <DataTable
            label="Mitglieder"
            rows={t.members}
            rowKey={(m) => m.userId}
            rowHref={(m) => paths.player(m.userId)}
            empty="Keine Mitglieder."
            columns={[
              { key: 'name', header: 'Spieler', cell: (m) => <>{m.name ?? '–'} {m.isOwner && <Tag tone="neutral">Besitzer</Tag>}</> },
              { key: 'joined', header: 'beigetreten', hide: 'sm', cell: (m) => <Time iso={m.joinedAt} /> },
              {
                key: 'act',
                header: '',
                num: true,
                cell: (m) => (
                  <Button
                    variant="danger"
                    className="compact"
                    onClick={() => setAction({ remove: { userId: m.userId, name: m.name ?? m.userId, isOwner: m.isOwner } })}
                  >
                    Entfernen…
                  </Button>
                ),
              },
            ]}
          />
        )}
        {tab === 'chests' && <ChestTab query={{ teamId: id }} />}
        {tab === 'areas' && <AreasTab query={{ teamId: id }} />}
        {tab === 'plans' && <PlansTab query={{ teamId: id }} />}
        {tab === 'audit' && <AuditTab targetType="team" targetId={id} />}
      </div>

      {action === 'rename' && (
        <RenameDialog
          title="Team umbenennen"
          current={t.name}
          maxLength={32}
          onClose={() => setAction(null)}
          onSave={async (name, reason) => {
            qc.setQueryData(key, await api.patch<AdminTeamDetail>(`/admin/teams/${id}`, { name, reason }));
            await refresh();
            toast('Team umbenannt.');
          }}
        />
      )}
      {action === 'rotate' && (
        <ConfirmDialog
          open
          title="Join-ID erneuern"
          consequence="Die alte Join-ID wird sofort ungültig. Bestehende Mitglieder bleiben im Team."
          confirmLabel="Erneuern"
          onConfirm={async (reason) => {
            qc.setQueryData(key, await api.post<AdminTeamDetail>(`/admin/teams/${id}/join-code`, { reason }));
            await refresh();
            toast('Join-ID erneuert.');
          }}
          onClose={() => setAction(null)}
        />
      )}
      {action === 'dissolve' && (
        <ConfirmDialog
          open
          title="Team auflösen"
          consequence={`Löst „${t.name}“ mit ${t.members.length} Mitgliedern auf. Die ${t.chestCount} Kisten bleiben bei ihren Besitzern, das Team sieht sie aber nicht mehr gemeinsam. Nicht umkehrbar.`}
          confirmText={t.name}
          confirmLabel="Auflösen"
          onConfirm={async (reason) => {
            await api.delete(`/admin/teams/${id}`, { reason });
            await refresh();
            toast(`„${t.name}“ aufgelöst.`);
            navigate(paths.teams);
          }}
          onClose={() => setAction(null)}
        />
      )}
      {remove && (
        <ConfirmDialog
          open
          title="Mitglied entfernen"
          consequence={
            remove.isOwner
              ? `${remove.name} ist Besitzer. Das Team geht an das Mitglied, das am längsten dabei ist${t.members.length === 1 ? '; als letztes Mitglied wird das Team aufgelöst' : ''}.`
              : `${remove.name} verlässt das Team und nimmt die eigenen Kisten mit.`
          }
          confirmText={remove.name}
          confirmLabel="Entfernen"
          onConfirm={async (reason) => {
            const res = await api.delete<{ team: AdminTeamDetail | null }>(`/admin/teams/${id}/members/${remove.userId}`, { reason });
            await refresh();
            toast(`${remove.name} entfernt.`);
            if (res.team) qc.setQueryData(key, res.team);
            else navigate(paths.teams);
          }}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
